import React, { useState, useEffect, useCallback } from "react";
import api from "../api/homeapi";
import { StaffFrame, PageHead, T } from "./DashboardShared";
import { PinIcon, PlusIcon, TrashIcon, ImageIcon, UploadIcon, EditIcon, XIcon } from "../components/Icons";
import { formatDateFR } from "../utils/format";
import { useNotify, errorMessage, InlineAlert } from "../components/Notify";

const EMPTY_FORM = { title: "", body: "", imageUrl: "", published: true };

const css = `
.news-item{background:${T.white};border:1.5px solid ${T.creamDk};border-radius:14px;padding:14px;margin-bottom:12px;display:flex;gap:14px;align-items:flex-start;}
.news-img{width:96px;height:68px;object-fit:cover;border-radius:10px;flex-shrink:0;}
.news-main{flex:1;min-width:0;}
.news-title{font-weight:700;font-size:15px;line-height:1.35;overflow-wrap:anywhere;}
.news-body{font-size:13px;color:#4b5a53;margin:5px 0 6px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
.news-actions{display:flex;flex-direction:column;gap:6px;flex-shrink:0;}
.draft-pill{font-size:10px;background:#f1efe8;color:${T.smoke};border-radius:6px;padding:2px 7px;font-weight:700;letter-spacing:.05em;vertical-align:middle;margin-left:6px;}
.img-drop{display:flex;align-items:center;gap:12px;flex-wrap:wrap;}
.img-prev{height:64px;width:110px;object-fit:cover;border-radius:10px;border:1.5px solid ${T.creamDk};}
.img-empty{height:64px;width:110px;display:flex;align-items:center;justify-content:center;border:1.5px dashed ${T.creamDk};border-radius:10px;color:${T.smoke};}
@media(max-width:600px){
  .news-item{flex-wrap:wrap;}
  .news-img{width:100%;height:140px;}
  .news-actions{flex-direction:row;width:100%;border-top:1.5px dashed ${T.creamDk};padding-top:10px;}
  .news-actions .btn{flex:1;justify-content:center;}
}
`;

export default function AdminNews() {
  const notify = useNotify();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editingId, setEditingId] = useState(null); // null = pas d'édition, "new" = création
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/api/admin/news")
      .then(r => { setItems(Array.isArray(r.data) ? r.data : []); setLoadError(""); })
      .catch(e => setLoadError(errorMessage(e, "Impossible de charger les actualités.")))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setForm(EMPTY_FORM); setEditingId("new"); };
  const startEdit = (n) => { setForm({ title: n.title, body: n.body, imageUrl: n.imageUrl || "", published: n.published }); setEditingId(n.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setForm(EMPTY_FORM); };

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { notify.warning("Choisissez un fichier image (JPG, PNG, WebP…)."); return; }
    if (file.size > 400 * 1024) { notify.warning("Image trop lourde : 400 Ko maximum. Compressez-la puis réessayez.", { title: "Image refusée" }); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, imageUrl: reader.result }));
    reader.onerror = () => notify.error("Lecture de l'image impossible.");
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { notify.warning("Le titre et le contenu sont obligatoires.", { title: "Formulaire incomplet" }); return; }
    setSaving(true);
    try {
      if (editingId === "new") await api.post("/api/admin/news", form);
      else await api.put(`/api/admin/news/${editingId}`, form);
      notify.success(editingId === "new"
        ? (form.published ? "L'actualité est publiée sur le site." : "L'actualité est enregistrée en brouillon.")
        : "Les modifications ont été enregistrées.");
      cancel();
      load();
    } catch (e) {
      notify.error(errorMessage(e, "L'enregistrement a échoué."), { title: "Enregistrement impossible" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    const ok = await notify.confirm({
      title: "Supprimer cette actualité ?", danger: true, confirmLabel: "Supprimer",
      message: <>« <strong>{n.title}</strong> » sera retirée du site et supprimée définitivement.</>,
    });
    if (!ok) return;
    try { await api.delete(`/api/admin/news/${n.id}`); notify.success("Actualité supprimée."); load(); }
    catch (e) { notify.error(errorMessage(e, "La suppression a échoué.")); }
  };

  const togglePublished = async (n) => {
    try {
      await api.put(`/api/admin/news/${n.id}`, { published: !n.published });
      notify.success(n.published ? "L'actualité n'est plus visible sur le site." : "L'actualité est maintenant visible sur le site.");
      load();
    } catch (e) { notify.error(errorMessage(e, "Mise à jour impossible.")); }
  };

  return (
    <StaffFrame>
      <style>{css}</style>
      <div className="page narrow">
        <PageHead title="Actualités & Promotions" icon={<PinIcon size={22}/>}
          sub={'Visibles par tous les visiteurs sur la page d\'accueil (section « Actualités & Promotions »).'}>
          {editingId === null && <button className="btn btn-primary" onClick={startCreate}><PlusIcon size={14}/> Publier une actualité</button>}
        </PageHead>

        {loadError && <InlineAlert type="error" title="Chargement impossible">{loadError} <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={load}>Réessayer</button></InlineAlert>}

        {editingId !== null && (
          <div className="stf-card" style={{ animation: "fadeUp .3s ease both" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div className="stf-card-title" style={{ margin: 0 }}>{editingId === "new" ? "Nouvelle actualité" : "Modifier l'actualité"}</div>
              <button className="icon-btn" onClick={cancel} aria-label="Fermer le formulaire"><XIcon size={16}/></button>
            </div>
            <div className="form-stack">
              <div>
                <label className="dlabel">Titre</label>
                <input className="dinput" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex : Nouveaux horaires pour la ligne Libreville – Franceville" />
              </div>
              <div>
                <label className="dlabel">Contenu</label>
                <textarea className="dinput dtextarea" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Détails de l'actualité, de l'annonce ou de la promotion…" />
              </div>
              <div>
                <label className="dlabel">Image (optionnelle)</label>
                <div className="img-drop">
                  {form.imageUrl ? <img src={form.imageUrl} alt="" className="img-prev" /> : <div className="img-empty"><ImageIcon size={20}/></div>}
                  <label className="btn btn-ghost" style={{ cursor: "pointer" }}>
                    <UploadIcon size={13}/> Téléverser
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageFile} />
                  </label>
                  {form.imageUrl && <button className="btn btn-danger" onClick={() => setForm(f => ({ ...f, imageUrl: "" }))}>Retirer</button>}
                </div>
                <div className="hint">400 Ko maximum, format paysage conseillé.</div>
              </div>
              <label className="check-row">
                <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} />
                Publier immédiatement sur le site
              </label>
              <button className="btn btn-primary btn-full" onClick={save} disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
            </div>
          </div>
        )}

        {loading && <div className="loading-block"><div className="spinner"/>Chargement…</div>}
        {!loading && !loadError && items.length === 0 && (
          <div className="empty"><div className="empty-icon"><PinIcon size={28}/></div><p className="empty-text">Aucune actualité pour le moment.</p></div>
        )}
        {items.map(n => (
          <div key={n.id} className="news-item">
            {n.imageUrl && <img src={n.imageUrl} alt="" className="news-img" />}
            <div className="news-main">
              <div className="news-title">{n.title}{!n.published && <span className="draft-pill">BROUILLON</span>}</div>
              <p className="news-body">{n.body}</p>
              <div className="muted small">{formatDateFR(n.createdAt)}</div>
            </div>
            <div className="news-actions">
              <button className="btn btn-ghost btn-sm" onClick={() => togglePublished(n)}>{n.published ? "Dépublier" : "Publier"}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => startEdit(n)}><EditIcon size={12}/> Modifier</button>
              <button className="btn btn-danger btn-sm" onClick={() => remove(n)}><TrashIcon size={12}/> Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </StaffFrame>
  );
}
